import { useEffect, useState, useCallback } from 'react';
import { Code2, Copy, ExternalLink, X, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * JsonLdPreview
 * ─────────────
 * Developer / SEO-team debug widget that lists every JSON-LD script currently
 * injected on the page. Mount it once in App.js — it self-activates only when
 * the URL contains `?debug=schema`. Otherwise it renders nothing (zero perf
 * cost in production).
 *
 * Features:
 *   • Floating pill in the bottom-right showing schema-block count.
 *   • Click to expand a drawer listing every block with @type + parsed JSON.
 *   • Copy-to-clipboard per block.
 *   • Quick links to Google Rich Results Test for the current URL.
 *   • Live-updates via MutationObserver — pages that inject schema in
 *     useEffect (Dubai hub, visa guides, etc.) appear without a refresh.
 */

const isDebugMode = () => {
  if (typeof window === 'undefined') return false;
  const sp = new URLSearchParams(window.location.search);
  return sp.get('debug') === 'schema';
};

const summariseType = (block) => {
  // block.parsed is either an object or an array; extract @type(s) for the pill.
  if (!block.parsed) return 'INVALID';
  const items = Array.isArray(block.parsed) ? block.parsed : [block.parsed];
  const types = items
    .map((it) => {
      if (!it || typeof it !== 'object') return null;
      // @graph wrapper
      if (Array.isArray(it['@graph'])) return `@graph(${it['@graph'].length})`;
      return it['@type'] || '?';
    })
    .filter(Boolean);
  return types.join(' · ') || '?';
};

const scanDocument = () => {
  const scripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
  return scripts.map((el, i) => {
    let parsed = null;
    let parseError = null;
    try {
      parsed = JSON.parse(el.textContent || '{}');
    } catch (e) {
      parseError = e.message;
    }
    // Stable id — combine the data-* marker attrs if present (used by visa-guide shells)
    const dataMarker = Array.from(el.attributes)
      .filter((a) => a.name.startsWith('data-'))
      .map((a) => a.name)
      .join(',');
    const id = dataMarker || `inline-${i}`;
    return { id, raw: el.textContent || '', parsed, parseError };
  });
};

const JsonLdPreview = () => {
  const [active, setActive] = useState(isDebugMode());
  const [open, setOpen] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  /* Validation state: { [blockId]: { issues, error_count, warning_count } } */
  const [validation, setValidation] = useState({});
  const [validating, setValidating] = useState(false);

  // Re-check active state when URL changes (single-page nav).
  useEffect(() => {
    const onPop = () => setActive(isDebugMode());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Live-scan: initial + every <head> mutation.
  useEffect(() => {
    if (!active) return undefined;
    const refresh = () => setBlocks(scanDocument());
    refresh();
    const obs = new MutationObserver(refresh);
    obs.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [active]);

  // Whenever the block list changes, re-validate via the backend.
  useEffect(() => {
    if (!active || blocks.length === 0 || !API_BASE) {
      setValidation({});
      return undefined;
    }
    const controller = new AbortController();
    const payload = {
      blocks: blocks.map((b) => ({ id: b.id, parsed: b.parsed })),
    };
    setValidating(true);
    fetch(`${API_BASE}/api/seo/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const byId = {};
        for (const r of data.results) byId[r.id] = r;
        setValidation(byId);
      })
      .catch(() => {/* ignore — validator endpoint optional */})
      .finally(() => setValidating(false));
    return () => controller.abort();
  }, [active, blocks]);

  const toggleBlock = useCallback((id) => {
    setExpandedIds((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  const copyBlock = useCallback(async (id, raw) => {
    try {
      const pretty = JSON.stringify(JSON.parse(raw), null, 2);
      await navigator.clipboard.writeText(pretty);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1200);
    } catch {
      /* ignore — clipboard blocked in sandbox */
    }
  }, []);

  if (!active) return null;

  const validCount = blocks.filter((b) => !b.parseError).length;
  const errorCount = blocks.length - validCount;
  /* Validation totals (independent of parse errors) */
  const totalSchemaErrors = Object.values(validation).reduce((n, r) => n + (r?.error_count || 0), 0);
  const totalSchemaWarnings = Object.values(validation).reduce((n, r) => n + (r?.warning_count || 0), 0);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const rrtUrl = `https://search.google.com/test/rich-results?url=${encodeURIComponent(currentUrl)}`;

  return (
    <div className="fixed bottom-4 left-4 z-[9999] font-mono text-[12px]" data-testid="json-ld-preview-root">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 text-white shadow-xl border border-emerald-400/30 hover:border-emerald-400 transition-all"
          data-testid="json-ld-preview-fab"
        >
          <Code2 className="w-4 h-4 text-emerald-400" />
          <span className="font-bold tracking-wider uppercase">JSON-LD</span>
          <span className="bg-emerald-500 text-black text-[10px] font-bold rounded-full px-2 py-0.5">{blocks.length}</span>
          {errorCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {errorCount}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          className="w-[min(560px,calc(100vw-32px))] max-h-[min(70vh,640px)] flex flex-col bg-slate-950 text-slate-100 rounded-2xl shadow-2xl border border-emerald-400/30 overflow-hidden"
          data-testid="json-ld-preview-drawer"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-900 border-b border-slate-800">
            <div className="flex items-center gap-2.5 flex-wrap">
              <Code2 className="w-4 h-4 text-emerald-400" />
              <span className="font-bold tracking-wider uppercase text-[11px]">JSON-LD Schema Preview</span>
              <span className="bg-emerald-500 text-black text-[10px] font-bold rounded-full px-2 py-0.5">{blocks.length}</span>
              {errorCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{errorCount} parse</span>
              )}
              {totalSchemaErrors > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1" data-testid="json-ld-preview-total-errors">
                  <AlertTriangle className="w-3 h-3" /> {totalSchemaErrors}
                </span>
              )}
              {totalSchemaWarnings > 0 && (
                <span className="bg-amber-500 text-black text-[10px] font-bold rounded-full px-2 py-0.5" data-testid="json-ld-preview-total-warnings">
                  ⚠ {totalSchemaWarnings}
                </span>
              )}
              {totalSchemaErrors === 0 && totalSchemaWarnings === 0 && blocks.length > 0 && !validating && (
                <span className="bg-emerald-500/15 text-emerald-300 text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1 border border-emerald-400/30" data-testid="json-ld-preview-all-clean">
                  <ShieldCheck className="w-3 h-3" /> Clean
                </span>
              )}
              {validating && (
                <span className="text-slate-400 text-[10px]">validating…</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <a
                href={rrtUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in Google Rich Results Test"
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300 hover:text-emerald-400 transition-colors"
                data-testid="json-ld-preview-rrt"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close drawer"
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300 hover:text-white"
                data-testid="json-ld-preview-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="overflow-y-auto px-3 py-3 space-y-2">
            {blocks.length === 0 && (
              <div className="px-3 py-8 text-center text-slate-500">
                No JSON-LD blocks detected on this page.
              </div>
            )}
            {blocks.map((b) => {
              const isOpen = !!expandedIds[b.id];
              const summary = b.parseError ? 'PARSE ERROR' : summariseType(b);
              const v = validation[b.id];
              const hasSchemaIssues = v && (v.error_count > 0 || v.warning_count > 0);
              return (
                <div
                  key={b.id}
                  className={`border rounded-xl overflow-hidden ${
                    b.parseError || (v && v.error_count > 0)
                      ? 'border-red-500/50'
                      : (v && v.warning_count > 0) ? 'border-amber-500/40' : 'border-slate-800'
                  }`}
                  data-testid="json-ld-preview-item"
                >
                  <button
                    type="button"
                    onClick={() => toggleBlock(b.id)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {b.parseError ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      ) : v && v.error_count > 0 ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      ) : v && v.warning_count > 0 ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      )}
                      <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200 truncate">{summary}</span>
                      {v && v.error_count > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-px">{v.error_count}E</span>
                      )}
                      {v && v.warning_count > 0 && (
                        <span className="bg-amber-500 text-black text-[9px] font-bold rounded-full px-1.5 py-px">{v.warning_count}W</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 text-[10px]">
                      <span className="truncate max-w-[180px]" title={b.id}>{b.id}</span>
                      <span>{isOpen ? '▾' : '▸'}</span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="bg-slate-950 px-3 py-3 border-t border-slate-800">
                      {b.parseError && (
                        <div className="text-red-400 text-[11px] mb-2">⚠ {b.parseError}</div>
                      )}

                      {/* Schema validation issues */}
                      {hasSchemaIssues && (
                        <div className="mb-3 space-y-1.5" data-testid="json-ld-preview-issues">
                          {v.issues.map((iss, i) => (
                            <div
                              key={`${iss.kind}-${iss.path}-${i}`}
                              className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${
                                iss.kind === 'error'
                                  ? 'bg-red-500/10 border-red-500/30 text-red-200'
                                  : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                              }`}
                            >
                              <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${iss.kind === 'error' ? 'text-red-400' : 'text-amber-400'}`} />
                              <div className="text-[11px] leading-relaxed min-w-0">
                                <div className="font-bold uppercase tracking-wider text-[9px] opacity-70">{iss.kind} · {iss.path}</div>
                                <div>{iss.message}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {v && !hasSchemaIssues && (
                        <div className="flex items-center gap-1.5 text-emerald-300 text-[11px] mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Validation clean — all required fields present.</div>
                      )}

                      <pre className="text-[11px] leading-relaxed text-emerald-200 whitespace-pre-wrap break-words max-h-72 overflow-y-auto bg-slate-900/40 rounded-lg p-3 border border-slate-800">
                        {b.parseError ? b.raw : JSON.stringify(b.parsed, null, 2)}
                      </pre>
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => copyBlock(b.id, b.raw)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] uppercase tracking-wider font-bold"
                          data-testid="json-ld-preview-copy"
                        >
                          <Copy className="w-3 h-3" />
                          {copiedId === b.id ? 'Copied' : 'Copy JSON'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-slate-900 border-t border-slate-800 text-[10px] text-slate-500 flex items-center justify-between">
            <span>Live · MutationObserver scanning</span>
            <span>?debug=schema</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonLdPreview;
